import { useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { PostAnnotation } from '@/hooks/use-annotations';

interface AnnotationPinProps {
  annotation: PostAnnotation;
  onMarkSeen?: (annotationId: string) => void | Promise<void>;
  className?: string;
  pointerEvents?: 'auto' | 'none';
  align?: 'top' | 'bottom';
  sideOffset?: number;
}

interface AnnotationPositionMetadata {
  position?: {
    x?: number;
    y?: number;
  };
  side?: 'top' | 'bottom';
  hoverBehavior?: 'pin-only' | 'always';
}

export function AnnotationPin({
  annotation,
  onMarkSeen,
  className,
  pointerEvents = 'auto',
  align,
  sideOffset = 12,
}: AnnotationPinProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSeen, setHasSeen] = useState(!!annotation.has_seen);
  const [isPointerCoarse, setIsPointerCoarse] = useState(false);
  const [showFlash, setShowFlash] = useState(!annotation.has_seen);

  const metadata = (annotation.metadata ?? {}) as AnnotationPositionMetadata;
  const anchorSide = align ?? metadata.side ?? 'top';

  useEffect(() => {
    setHasSeen(!!annotation.has_seen);
  }, [annotation.has_seen, annotation.updated_at]);

  // Flash animation: show for 2 seconds, then fade out
  useEffect(() => {
    if (!annotation.has_seen && showFlash) {
      const timer = setTimeout(() => {
        setShowFlash(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [annotation.has_seen, showFlash]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(pointer: coarse)');
    const updatePointerState = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsPointerCoarse(event.matches);
    };

    updatePointerState(mediaQuery);

    mediaQuery.addEventListener?.('change', updatePointerState);

    return () => {
      mediaQuery.removeEventListener?.('change', updatePointerState);
    };
  }, []);

  const positionStyle = useMemo(() => {
    const style: React.CSSProperties = {};
    const x = metadata.position?.x;
    const y = metadata.position?.y;

    if (typeof x === 'number') {
      style.left = `${Math.min(100, Math.max(0, x))}%`;
      style.transform = 'translateX(-50%)';
    } else {
      style.right = '0.75rem';
    }

    if (typeof y === 'number') {
      style.top = `${Math.min(100, Math.max(0, y))}%`;
      style.transform = style.transform ? `${style.transform} translateY(-50%)` : 'translateY(-50%)';
    } else {
      style.top = '0.75rem';
    }

    return style;
  }, [metadata.position?.x, metadata.position?.y]);

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen && !hasSeen) {
      setHasSeen(true);
      const maybePromise = onMarkSeen?.(annotation.id);
      if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
        (maybePromise as Promise<void>).catch((error) => {
          console.error('[AnnotationPin] markSeen failed', error);
        });
      }
    }
  };

  const handleMouseEnter = () => {
    if (!isPointerCoarse) {
      handleOpenChange(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPointerCoarse) {
      handleOpenChange(false);
    }
  };

  const updatedLabel = useMemo(() => {
    if (!annotation.updated_at || !annotation.created_at) {
      return null;
    }

    const created = new Date(annotation.created_at);
    const updated = new Date(annotation.updated_at);

    if (!Number.isFinite(created.valueOf()) || !Number.isFinite(updated.valueOf())) {
      return null;
    }

    if (Math.abs(updated.getTime() - created.getTime()) < 1_000) {
      return `Added ${formatDistanceToNow(created, { addSuffix: true })}`;
    }

    return `Updated ${formatDistanceToNow(updated, { addSuffix: true })}`;
  }, [annotation.created_at, annotation.updated_at]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={positionStyle}
          className={cn(
            'absolute inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-primary text-white shadow-lg transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 pointer-events-auto',
            showFlash ? 'opacity-100 scale-110' : 'opacity-0 scale-90',
            pointerEvents === 'none' && 'pointer-events-none',
            className
          )}
        >
          {showFlash && (
            <span className="absolute inline-flex h-10 w-10 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60" />
            </span>
          )}
          <MessageSquare className="h-4 w-4 fill-current" />
          <span className="sr-only">View annotation</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side={anchorSide}
        sideOffset={sideOffset}
        className="max-w-xs rounded-lg border border-[#343536] bg-[#1F1F23] p-3 text-left text-sm text-[#E5E7EB] shadow-xl"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-primary/70">Artist note</div>
          <p className="whitespace-pre-wrap leading-snug text-[#F4F4F5]">{annotation.content}</p>
          {updatedLabel ? (
            <div className="text-[11px] text-muted-foreground">{updatedLabel}</div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AnnotationPin;
